'use client';

import { useState } from 'react';
import Image from 'next/image';
import { usePathname, useSearchParams } from 'next/navigation';
import { allowedFilterParams } from '@api/allowedParams';
import { paginationPages } from '@/src/utils/paginationPages';
import Grid from '@components/Grid';
import Pagination from '@components/Pagination';
import Filter from '@components/Filter/Filter';
import ActiveFilters from '@components/Filter/ActiveFilters';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next-intl/link';
import { useLocaleRoutes } from '@/src/utils/useLocaleRoutes';
import { useRouter } from 'next/navigation';
import getMainCategories, {
  Category,
  getCategoryBySlug,
} from '@/src/utils/getMainCategories';
import Sorting from '@components/Sorting';
import getOption from '@/src/utils/getOption';
import replaceContact from '@/src/utils/replaceContact';
import SubcategoriesNavbar from '@components/SubcategoriesNavbar';
import BannerFrontBottomFixed from '@components/Banners/BannerFrontBottomFixed';
import getDefaultSorting from '@/src/utils/getDefaultSorting';

export default function ClientPage({
  slug,
  data,
  subCat = false,
  categoriesData,
}: {
  slug: string;
  data: any;
  subCat?: any;
  categoriesData: any;
}) {
  const t = useTranslations('page.category');
  const path = useLocaleRoutes();
  const pathname = usePathname();
  const { push } = useRouter();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const q = (param: string): string => {
    return searchParams.get(param) ? String(searchParams.get(param)) : '';
  };
  const [sorting, setSorting] = useState(
    q('sort') ? q('sort') : getDefaultSorting()
  );
  const defaultSorting = !q('sort');
  const categories = getMainCategories();

  let category = null as null | Category;
  categories.forEach((cat) => {
    if (slug === cat.slug) {
      category = cat;
    }
  });
  if (!category && slug !== 'all') {
    push(`/${path('all')}`);
  }
  const categoryId = category?.id;
  const catData = getCategoryBySlug(String(category?.slug));
  const subCatTitle = subCat?.['name-' + locale];

  let activeFilters = false;
  let filterParams = {} as any;
  if (categoryId) {
    filterParams['category.parent_id'] = categoryId;
  }
  allowedFilterParams.forEach((param) => {
    if (searchParams.get(param)) {
      filterParams[param] = searchParams.get(param);
      activeFilters = true;
    }
  });

  const currentQuery = new URLSearchParams(Array.from(searchParams.entries()));
  const currentPage = parseInt(q('page')) > 0 ? parseInt(q('page')) : 1;

  let pages = [] as number[];
  if (data && data.meta?.hasOwnProperty('last_page')) {
    pages = paginationPages(data.meta.current_page, data.meta.last_page);
  }

  const updateSortingQuery = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSorting(e.currentTarget.value);
    const currentQuery = new URLSearchParams(
      Array.from(searchParams.entries())
    );
    currentQuery.set('sort', e.currentTarget.value);
    currentQuery.delete('page');
    push(`${pathname}?${currentQuery.toString()}`);
  };

  const customTitle = catData['title-' + locale];
  const title = catData['name-' + locale];
  const concluded =
    searchParams.get('auctions') === '4,8' ? t('concluded') + ' ' : '';

  return (
    <>
      <SubcategoriesNavbar categoriesData={categoriesData} />
      <div className='mt-4 flex flex-col gap-y-6'>
        <div className='flex flex-wrap items-center justify-between gap-4 border-b-4 border-brand pb-4'>
          <h1 className='text-xl uppercase text-[#b2b2b2] md:text-3xl'>
            {!concluded && customTitle}
            {!concluded && customTitle && subCatTitle && ` - ${subCatTitle}`}
            {(concluded || (!concluded && !customTitle)) && (
              <>
                {concluded}
                {slug === 'all' &&
                  t(`header${getOption('HAS_AUCTIONS') ? 'Auctions' : ''}All`)}
                {slug !== 'all' &&
                  (subCatTitle
                    ? t(
                        `header${getOption('HAS_AUCTIONS') ? 'Auctions' : ''}WithSub`,
                        {
                          category: title,
                          subCategory: subCatTitle,
                        }
                      )
                    : t(
                        `header${getOption('HAS_AUCTIONS') ? 'Auctions' : ''}`,
                        {
                          category: title,
                        }
                      ))}
              </>
            )}
          </h1>
          <div className='relative ml-auto grid w-full grid-cols-2 items-center gap-4 sm:gap-y-10 md:flex md:w-auto md:gap-4 lg:grid lg:grid-cols-3'>
            {getOption('HAS_AUCTIONS') ? (
              <Link
                href={`/${path('all')}?auctions=4,8`}
                className='col-span-2 font-bold text-[#ddd] underline hover:text-white focus:text-white lg:col-span-1'
              >
                {t('showConcluded')}
              </Link>
            ) : (
              <div className='col-span-1 hidden lg:block'></div>
            )}
            <Sorting
              sorting={sorting}
              onChange={(e) => updateSortingQuery(e)}
              defaultSorting={defaultSorting}
            />
            <Filter
              slug={slug}
              category={category}
              subCat={subCat}
              categoriesData={categoriesData}
            />
          </div>
        </div>
        {catData['description-' + locale]}
        {activeFilters && <ActiveFilters categoryId={categoryId} />}
      </div>
      {!!data && !!data.meta?.total ? (
        <div className='my-4 text-sm'>
          {t('showingResults', {
            from: data.meta.from,
            to: data.meta.to,
            total: data.meta.total,
          })}
        </div>
      ) : (
        /* empty div for flexbox reasons */
        <div />
        /** **/
      )}
      {!!data?.data?.length && <Grid items={data.data} />}
      {!data?.data?.length && (
        <div className='relative mt-6 flex flex-col items-center items-center justify-center p-10 lg:min-h-[40vh]'>
          <Image
            src='/images/landscape.jpg'
            alt=''
            fill
            style={{ objectFit: 'cover', objectPosition: 'center' }}
            className='absolute z-0'
          />
          <div className='relative z-10 mb-6 flex max-w-[600px] flex-wrap items-center justify-center gap-12 lg:flex-nowrap'>
            <Image
              src='/images/sad-search.svg'
              alt=''
              width={150}
              height={150}
              className='h-auto w-[120px]'
            />
            <div>
              <h2 className='mb-2 text-2xl font-semibold md:text-4xl'>
                {t('noResults')}
              </h2>
              {getOption('SIBLING_SITE_URL') && (
                <span
                  dangerouslySetInnerHTML={{
                    __html: replaceContact(t('noResultsLink')),
                  }}
                ></span>
              )}
            </div>
          </div>
        </div>
      )}
      {pages.length > 1 && (
        <Pagination
          pages={pages}
          currentPage={currentPage}
          query={currentQuery}
        />
      )}
      {!getOption('HAS_AUCTIONS') && !!getOption('SIBLING_SITE_URL') && (
        <>
          <div className='mt-12' />
          <BannerFrontBottomFixed />
        </>
      )}
    </>
  );
}
